import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  markSourceWatchItemViewed,
  reviewSourceWatchItem,
  type CatalogItem,
  type SafetyTopic,
  type SourceWatchItem,
  type SourceWatchReviewInput,
  type SourceWatchSource,
} from './api';

type Props = {
  busy: boolean;
  csrfToken: string;
  equipmentTypes: CatalogItem[];
  items: SourceWatchItem[];
  onError: (error: unknown) => void;
  onRefresh: () => Promise<void>;
  onRun: (action: () => Promise<void>, message: string) => Promise<void>;
  sources: SourceWatchSource[];
  topics: SafetyTopic[];
  workTypes: CatalogItem[];
};

type ReviewFilter = 'ALL' | 'APPROVED' | 'NEW' | 'OPENED' | 'REJECTED';
type PermissionFilter = 'ALL' | 'BLOCKED' | 'PENDING' | 'USABLE';

const seasonTags = [
  ['SPRING_THAW', '春の融雪'],
  ['SUMMER_HEAT', '夏季高温'],
  ['AUTUMN_EARLY_DARK', '秋の日没早期化'],
  ['WINTER_FREEZE', '冬季凍結'],
  ['SNOW_REMOVAL', '除雪'],
  ['BLIZZARD', '吹雪'],
  ['ICY_SURFACE', '路面凍結'],
  ['YEAR_END_RUSH', '年末繁忙'],
] as const;

const regionTags = [
  ['NATIONAL', '全国'],
  ['HOKKAIDO', '北海道'],
  ['OTHER_REGION', '他地域'],
  ['MULTI_REGION', '複数地域'],
] as const;

const rejectReasons = [
  ['OUT_OF_SCOPE', '教材の対象外'],
  ['DUPLICATE', '既存情報と重複'],
  ['UNVERIFIABLE', '原典を確認できない'],
  ['UNSUITABLE_FOR_TRAINING', '教材への展開に不向き'],
  ['OUTDATED', '情報が古い'],
  ['OTHER', 'その他'],
] as const satisfies ReadonlyArray<
  readonly [NonNullable<SourceWatchItem['rejectReasonCode']>, string]
>;

const today = new Date().toISOString().slice(0, 10);
const nextYear = `${Number(today.slice(0, 4)) + 1}${today.slice(4)}`;

function formText(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

function nullable(data: FormData, name: string): string | null {
  return formText(data, name) || null;
}

function lines(data: FormData, name: string): string[] {
  return formText(data, name)
    .split(/\r?\n/u)
    .map((value) => value.trim())
    .filter(Boolean);
}

function daysSince(value: string, now = Date.now()): number {
  return Math.max(0, Math.floor((now - Date.parse(value)) / 86_400_000));
}

function permissionState(source: SourceWatchSource | undefined): Exclude<PermissionFilter, 'ALL'> {
  if (
    !source ||
    source.commercialUseStatus === 'PROHIBITED' ||
    source.contentUsagePolicy === 'REFERENCE_ONLY'
  ) {
    return 'BLOCKED';
  }
  if (
    source.permissionRequired === 1 ||
    source.commercialUseStatus !== 'CONFIRMED' ||
    source.contentUsagePolicy === 'MANUAL_PERMISSION_REQUIRED'
  ) {
    return 'PENDING';
  }
  return 'USABLE';
}

function reviewState(item: SourceWatchItem): Exclude<ReviewFilter, 'ALL'> {
  if (item.administratorStatus === 'APPROVED') return 'APPROVED';
  if (item.administratorStatus === 'REJECTED') return 'REJECTED';
  return item.firstViewedAt === null ? 'NEW' : 'OPENED';
}

function reviewLabel(item: SourceWatchItem): string {
  const state = reviewState(item);
  if (state === 'NEW') return '新着・未確認';
  if (state === 'OPENED') return '原典確認済み・審査待ち';
  if (state === 'APPROVED') return '承認済み';
  return '不採用';
}

function reviewChipClass(item: SourceWatchItem): string {
  const state = reviewState(item);
  if (state === 'APPROVED') return 'approved';
  if (state === 'REJECTED') return 'rejected';
  return state === 'NEW' ? 'warning' : 'in_review';
}

function sourcePriority(source: SourceWatchSource | undefined): number {
  return { CRITICAL: 0, HIGH: 1, NORMAL: 2, REFERENCE: 3 }[source?.priority ?? 'REFERENCE'];
}

function rejectReasonLabel(code: SourceWatchItem['rejectReasonCode']): string {
  return rejectReasons.find(([value]) => value === code)?.[1] ?? '理由未登録';
}

export function SourceReviewPanel({
  busy,
  csrfToken,
  equipmentTypes,
  items,
  onError,
  onRefresh,
  onRun,
  sources,
  topics,
  workTypes,
}: Props) {
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('ALL');
  const [permissionFilter, setPermissionFilter] = useState<PermissionFilter>('ALL');
  const [sourceId, setSourceId] = useState('');
  const [category, setCategory] = useState('');
  const [topicId, setTopicId] = useState('');
  const [region, setRegion] = useState('');
  const [publishedAfter, setPublishedAfter] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(items[0]?.id ?? null);
  const [formError, setFormError] = useState<string | null>(null);

  const sourcesById = useMemo(
    () => new Map(sources.map((source) => [source.id, source])),
    [sources],
  );
  const categories = useMemo(
    () => [...new Set(items.map((item) => item.category))].sort(),
    [items],
  );
  const filteredItems = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ja-JP');
    return items
      .filter((item) => reviewFilter === 'ALL' || reviewState(item) === reviewFilter)
      .filter((item) => !sourceId || item.sourceId === sourceId)
      .filter((item) => !category || item.category === category)
      .filter((item) => !topicId || item.topicIds.includes(topicId))
      .filter((item) => !region || item.regionTags.includes(region as never))
      .filter(
        (item) =>
          permissionFilter === 'ALL' ||
          permissionState(sourcesById.get(item.sourceId)) === permissionFilter,
      )
      .filter(
        (item) =>
          !publishedAfter ||
          (item.publishedOrUpdatedOn !== null && item.publishedOrUpdatedOn >= publishedAfter),
      )
      .filter((item) => {
        if (!needle) return true;
        return [item.rawTitle, item.sourceTitle, item.discoverySnippet, item.citationLabel]
          .filter((value): value is string => value !== null)
          .some((value) => value.toLocaleLowerCase('ja-JP').includes(needle));
      })
      .sort((left, right) => {
        const stateOrder = { NEW: 0, OPENED: 1, APPROVED: 2, REJECTED: 3 };
        const stateDifference = stateOrder[reviewState(left)] - stateOrder[reviewState(right)];
        if (stateDifference !== 0) return stateDifference;
        const priorityDifference =
          sourcePriority(sourcesById.get(left.sourceId)) -
          sourcePriority(sourcesById.get(right.sourceId));
        return priorityDifference || right.detectedAt.localeCompare(left.detectedAt);
      });
  }, [
    category,
    items,
    permissionFilter,
    publishedAfter,
    query,
    region,
    reviewFilter,
    sourceId,
    sourcesById,
    topicId,
  ]);

  useEffect(() => {
    if (selectedId === null || !filteredItems.some((item) => item.id === selectedId)) {
      setSelectedId(filteredItems[0]?.id ?? null);
    }
  }, [filteredItems, selectedId]);

  const selected = items.find((item) => item.id === selectedId) ?? null;
  const selectedSource = selected ? sourcesById.get(selected.sourceId) : undefined;
  const duplicate = selected?.possibleDuplicateOfItemId
    ? (items.find((item) => item.id === selected.possibleDuplicateOfItemId) ?? null)
    : null;
  const now = Date.now();
  const dashboard = {
    accident: items.filter((item) => /事故|災害|建設/u.test(item.category)).length,
    errors: sources.filter((source) => source.lastErrorAt !== null || source.pausedAt !== null)
      .length,
    hokkaido: items.filter(
      (item) =>
        item.regionTags.includes('HOKKAIDO') ||
        sourcesById.get(item.sourceId)?.regionScope === 'HOKKAIDO',
    ).length,
    newItems: items.filter((item) => reviewState(item) === 'NEW').length,
    pending: items.filter((item) => item.administratorStatus === 'PENDING_REVIEW').length,
    permissionPending: items.filter(
      (item) => permissionState(sourcesById.get(item.sourceId)) === 'PENDING',
    ).length,
    stale: sources.filter(
      (source) =>
        source.approvalStatus === 'APPROVED' &&
        source.checkEnabled === 1 &&
        (source.lastCheckedAt === null || daysSince(source.lastCheckedAt, now) >= 30),
    ).length,
  };

  async function submitApproval(event: FormEvent<HTMLFormElement>, item: SourceWatchItem) {
    event.preventDefault();
    setFormError(null);
    const data = new FormData(event.currentTarget);
    const monthValues = formText(data, 'seasonMonths')
      .split(/[、,\s]+/u)
      .filter(Boolean)
      .map(Number);
    if (
      monthValues.some((month) => !Number.isInteger(month) || month < 1 || month > 12) ||
      new Set(monthValues).size !== monthValues.length
    ) {
      setFormError('対象月は1〜12を重複なしで入力してください。例: 12,1,2');
      return;
    }
    const quizCandidates = lines(data, 'quizCandidates').map((line) => {
      const [question, expectedAnswer, rationale, ...extra] = line
        .split(/[|｜]/u)
        .map((value) => value.trim());
      if (!question || !expectedAnswer || !rationale || extra.length > 0) return null;
      return { expectedAnswer, question, rationale };
    });
    if (quizCandidates.some((candidate) => candidate === null)) {
      setFormError('確認問題候補は「質問｜正答｜根拠」を1行1問で入力してください。');
      return;
    }
    const workTypeId = formText(data, 'workTypeId');
    const equipmentTypeId = formText(data, 'equipmentTypeId');
    const input: SourceWatchReviewInput = {
      accidentPattern: nullable(data, 'accidentPattern'),
      citationLabel: formText(data, 'citationLabel'),
      discussionPoints: lines(data, 'discussionPoints'),
      effectiveFrom: formText(data, 'effectiveFrom'),
      equipmentTypeIds: equipmentTypeId ? [equipmentTypeId] : [],
      keyPoints: lines(data, 'keyPoints'),
      narrationSupport: lines(data, 'narrationSupport'),
      notes: nullable(data, 'notes'),
      preventiveActions: lines(data, 'preventiveActions'),
      publishedOrUpdatedOn: formText(data, 'publishedOrUpdatedOn'),
      quizCandidates: quizCandidates.filter(
        (candidate): candidate is NonNullable<typeof candidate> => candidate !== null,
      ),
      regionTags: data.getAll('regionTags').map(String) as SourceWatchItem['regionTags'],
      rootCauses: lines(data, 'rootCauses'),
      seasonMonths: monthValues,
      seasonTags: data.getAll('seasonTags').map(String) as SourceWatchItem['seasonTags'],
      siteApplicationPoints: lines(data, 'siteApplicationPoints'),
      status: 'APPROVED',
      summary: formText(data, 'summary'),
      topicIds: [formText(data, 'topicId')],
      validUntil: formText(data, 'validUntil'),
      workTypeIds: workTypeId ? [workTypeId] : [],
    };
    await onRun(async () => {
      await reviewSourceWatchItem(item.id, input, csrfToken);
    }, '原典確認済みの情報を承認し、最新安全情報へ登録しました。');
  }

  async function submitRejection(event: FormEvent<HTMLFormElement>, item: SourceWatchItem) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await onRun(async () => {
      await reviewSourceWatchItem(
        item.id,
        {
          notes: nullable(data, 'rejectNotes'),
          reasonCode: formText(data, 'reasonCode') as NonNullable<
            SourceWatchItem['rejectReasonCode']
          >,
          status: 'REJECTED',
        },
        csrfToken,
      );
    }, '理由を記録して検知情報を不採用にしました。');
  }

  return (
    <section className="stack source-review-panel" aria-labelledby="source-review-heading">
      <div className="section-heading">
        <div>
          <h3 id="source-review-heading">新着・審査待ち</h3>
          <p className="muted">
            原典を別タブで確認し、人が入力・承認した内容だけを教材候補にします。外部ページのHTMLや画像はこの画面へ埋め込みません。
          </p>
        </div>
      </div>

      <div className="source-review-metrics" aria-label="Source Watch集計">
        <button onClick={() => setReviewFilter('NEW')} type="button">
          <strong>{dashboard.newItems}</strong>
          <span>新着</span>
        </button>
        <button onClick={() => setReviewFilter('OPENED')} type="button">
          <strong>{dashboard.pending}</strong>
          <span>審査待ち</span>
        </button>
        <div>
          <strong>{dashboard.hokkaido}</strong>
          <span>北海道関連</span>
        </div>
        <div>
          <strong>{dashboard.accident}</strong>
          <span>工事・事故関連</span>
        </div>
        <div>
          <strong>{dashboard.permissionPending}</strong>
          <span>利用条件確認待ち</span>
        </div>
        <div>
          <strong>{dashboard.errors}</strong>
          <span>Watch ERROR</span>
        </div>
        <div>
          <strong>{dashboard.stale}</strong>
          <span>30日以上未確認Source</span>
        </div>
      </div>

      <div className="card source-review-filters">
        <label>
          審査状態
          <select
            value={reviewFilter}
            onChange={(event) => setReviewFilter(event.target.value as ReviewFilter)}
          >
            <option value="ALL">すべて</option>
            <option value="NEW">新着・未確認</option>
            <option value="OPENED">原典確認済み・審査待ち</option>
            <option value="APPROVED">承認済み</option>
            <option value="REJECTED">不採用</option>
          </select>
        </label>
        <label>
          発行元
          <select value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
            <option value="">すべて</option>
            {sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          カテゴリ
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">すべて</option>
            {categories.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          テーマ
          <select value={topicId} onChange={(event) => setTopicId(event.target.value)}>
            <option value="">すべて</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          地域
          <select value={region} onChange={(event) => setRegion(event.target.value)}>
            <option value="">すべて</option>
            {regionTags.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          利用条件
          <select
            value={permissionFilter}
            onChange={(event) => setPermissionFilter(event.target.value as PermissionFilter)}
          >
            <option value="ALL">すべて</option>
            <option value="USABLE">現行policyで利用可</option>
            <option value="PENDING">確認待ち</option>
            <option value="BLOCKED">利用不可</option>
          </select>
        </label>
        <label>
          公表日（以降）
          <input
            type="date"
            value={publishedAfter}
            onChange={(event) => setPublishedAfter(event.target.value)}
          />
        </label>
        <label>
          キーワード
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="タイトル・発行元・概要"
          />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="card empty-state">検知情報はまだありません。</div>
      ) : (
        <div className="source-review-workspace">
          <div className="card source-review-queue" aria-label="審査キュー">
            <div className="source-review-queue-summary">{filteredItems.length}件を表示</div>
            {filteredItems.length === 0 ? (
              <div className="empty-state">条件に一致する情報はありません。</div>
            ) : (
              filteredItems.map((item) => {
                const source = sourcesById.get(item.sourceId);
                const overdue =
                  item.administratorStatus === 'PENDING_REVIEW' &&
                  daysSince(item.detectedAt, now) >= 30;
                return (
                  <button
                    className={`source-review-queue-item${item.id === selectedId ? ' active' : ''}`}
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    type="button"
                  >
                    <span className="source-review-queue-topline">
                      <span className={`status-chip ${reviewChipClass(item)}`}>
                        {reviewLabel(item)}
                      </span>
                      <span>{source?.priority ?? 'REFERENCE'}</span>
                    </span>
                    <strong>{item.rawTitle}</strong>
                    <small>
                      {item.sourceTitle} · {item.publishedOrUpdatedOn ?? '公表日未確認'}
                    </small>
                    <small>
                      検知から{daysSince(item.detectedAt, now)}日{overdue ? ' · 30日超過' : ''}
                    </small>
                  </button>
                );
              })
            )}
          </div>

          <div className="card stack source-review-detail">
            {selected === null ? (
              <div className="empty-state">左の一覧から確認する情報を選択してください。</div>
            ) : (
              <>
                <div className="content-row source-review-title">
                  <div>
                    <span className={`status-chip ${reviewChipClass(selected)}`}>
                      {reviewLabel(selected)}
                    </span>
                    <h3>{selected.rawTitle}</h3>
                    <small>
                      {selected.sourceTitle} · {selected.category}
                    </small>
                  </div>
                </div>

                {selected.administratorStatus === 'PENDING_REVIEW' &&
                  daysSince(selected.detectedAt, now) >= 30 && (
                    <div className="notice warning">
                      検知から30日以上経過しています。原典の更新有無と現在の有効性を優先確認してください。
                    </div>
                  )}
                {selected.discoverySnippet && (
                  <div className="source-review-snippet">
                    <strong>取得時のplain text概要</strong>
                    <p>{selected.discoverySnippet}</p>
                  </div>
                )}

                <dl className="source-review-metadata">
                  <div>
                    <dt>発行元</dt>
                    <dd>{selectedSource?.issuer ?? selected.sourceTitle}</dd>
                  </div>
                  <div>
                    <dt>公表・更新日</dt>
                    <dd>{selected.publishedOrUpdatedOn ?? selected.updatedAtSource ?? '未確認'}</dd>
                  </div>
                  <div>
                    <dt>検知日時</dt>
                    <dd>{selected.detectedAt}</dd>
                  </div>
                  <div>
                    <dt>content type</dt>
                    <dd>{selected.contentType ?? '不明'}</dd>
                  </div>
                  <div>
                    <dt>external ID</dt>
                    <dd>{selected.externalId ?? 'なし'}</dd>
                  </div>
                  <div>
                    <dt>変更hash</dt>
                    <dd>
                      <code>{selected.contentHash}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>利用policy</dt>
                    <dd>
                      {selectedSource?.contentUsagePolicy ?? '未設定'} /{' '}
                      {selectedSource?.commercialUseStatus ?? '未設定'}
                    </dd>
                  </div>
                  <div>
                    <dt>原典確認</dt>
                    <dd>{selected.firstViewedAt ?? '未確認'}</dd>
                  </div>
                </dl>

                <a
                  className="button-link source-original-link"
                  href={selected.canonicalItemUrl ?? selected.sourceUrl}
                  onClick={() => {
                    if (
                      selected.administratorStatus === 'PENDING_REVIEW' &&
                      selected.firstViewedAt === null
                    ) {
                      void markSourceWatchItemViewed(selected.id, csrfToken)
                        .then(onRefresh)
                        .catch(onError);
                    }
                  }}
                  rel="noreferrer"
                  target="_blank"
                >
                  原典を別タブで開いて確認
                </a>

                {selected.possibleDuplicateOfItemId && (
                  <div className="source-duplicate-compare">
                    <div>
                      <span>今回の検知</span>
                      <strong>{selected.rawTitle}</strong>
                      <small>
                        {selected.publishedOrUpdatedOn ?? '日付未確認'} ·{' '}
                        {selected.contentHash.slice(0, 12)}…
                      </small>
                    </div>
                    <div>
                      <span>重複候補（自動統合しません）</span>
                      <strong>{duplicate?.rawTitle ?? '候補を一覧で確認できません'}</strong>
                      <small>
                        {duplicate?.publishedOrUpdatedOn ?? '日付未確認'} ·{' '}
                        {duplicate?.contentHash.slice(0, 12) ?? 'hash不明'}…
                      </small>
                    </div>
                  </div>
                )}

                {selected.administratorStatus === 'PENDING_REVIEW' ? (
                  <>
                    <form
                      className="stack source-review-form"
                      key={selected.id}
                      onSubmit={(event) => void submitApproval(event, selected)}
                    >
                      <h4>承認内容</h4>
                      <label>
                        出典表示名
                        <input defaultValue={selected.sourceTitle} name="citationLabel" required />
                      </label>
                      <label>
                        管理者要約
                        <textarea name="summary" required rows={5} />
                      </label>
                      <label>
                        重要ポイント（1行1項目）
                        <textarea name="keyPoints" required rows={5} />
                      </label>
                      <label>
                        適用テーマ
                        <select name="topicId" required defaultValue="">
                          <option disabled value="">
                            選択してください
                          </option>
                          {topics.map((topic) => (
                            <option key={topic.id} value={topic.id}>
                              {topic.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        事故パターン（該当する場合）
                        <textarea name="accidentPattern" rows={3} />
                      </label>
                      <div className="form-grid compact-grid">
                        <label>
                          根本原因（1行1項目）
                          <textarea name="rootCauses" rows={4} />
                        </label>
                        <label>
                          防止対策（1行1項目）
                          <textarea name="preventiveActions" rows={4} />
                        </label>
                        <label>
                          討議ポイント（1行1項目）
                          <textarea name="discussionPoints" rows={4} />
                        </label>
                        <label>
                          現場への適用（1行1項目）
                          <textarea name="siteApplicationPoints" rows={4} />
                        </label>
                        <label>
                          ナレーション補助（1行1項目）
                          <textarea name="narrationSupport" rows={4} />
                        </label>
                        <label>
                          確認問題候補（質問｜正答｜根拠）
                          <textarea name="quizCandidates" rows={4} />
                        </label>
                      </div>
                      <div className="form-grid compact-grid">
                        <label>
                          対象工種
                          <select name="workTypeId">
                            <option value="">全工種</option>
                            {workTypes.map((value) => (
                              <option key={value.id} value={value.id}>
                                {value.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          対象機械
                          <select name="equipmentTypeId">
                            <option value="">指定なし</option>
                            {equipmentTypes.map((value) => (
                              <option key={value.id} value={value.id}>
                                {value.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          対象月（例: 12,1,2）
                          <input name="seasonMonths" placeholder="通年は空欄" />
                        </label>
                      </div>
                      <fieldset>
                        <legend>季節条件（複数選択可）</legend>
                        <div className="check-grid">
                          {seasonTags.map(([value, label]) => (
                            <label key={value}>
                              <input name="seasonTags" type="checkbox" value={value} /> {label}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <fieldset>
                        <legend>地域（複数選択可）</legend>
                        <div className="check-grid">
                          {regionTags.map(([value, label]) => (
                            <label key={value}>
                              <input name="regionTags" type="checkbox" value={value} /> {label}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <div className="form-grid compact-grid">
                        <label>
                          公表・更新日
                          <input
                            defaultValue={selected.publishedOrUpdatedOn ?? today}
                            name="publishedOrUpdatedOn"
                            required
                            type="date"
                          />
                        </label>
                        <label>
                          有効開始
                          <input defaultValue={today} name="effectiveFrom" required type="date" />
                        </label>
                        <label>
                          有効期限
                          <input defaultValue={nextYear} name="validUntil" required type="date" />
                        </label>
                      </div>
                      <label>
                        管理メモ（任意）
                        <textarea name="notes" rows={3} />
                      </label>
                      {selected.firstViewedAt === null && (
                        <div className="notice warning">
                          承認前に上の「原典を別タブで開いて確認」を実行してください。
                        </div>
                      )}
                      {formError && <div className="notice error">{formError}</div>}
                      <button disabled={busy || selected.firstViewedAt === null}>
                        原典・入力内容を確認済みとして承認
                      </button>
                    </form>

                    <form
                      className="source-reject-form"
                      onSubmit={(event) => void submitRejection(event, selected)}
                    >
                      <label>
                        不採用理由
                        <select name="reasonCode" required defaultValue="OUT_OF_SCOPE">
                          {rejectReasons.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        補足（「その他」は必須）
                        <input name="rejectNotes" />
                      </label>
                      <button className="danger-link" disabled={busy}>
                        理由を記録して不採用
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="source-review-result">
                    <h4>
                      {selected.administratorStatus === 'APPROVED' ? '承認済み内容' : '不採用記録'}
                    </h4>
                    {selected.administratorStatus === 'REJECTED' ? (
                      <>
                        <p>
                          <strong>{rejectReasonLabel(selected.rejectReasonCode)}</strong>
                        </p>
                        <p>{selected.reviewNotes ?? '補足なし'}</p>
                      </>
                    ) : (
                      <>
                        <p>{selected.summary ?? '要約なし'}</p>
                        {selected.keyPoints.length > 0 && (
                          <ul>
                            {selected.keyPoints.map((point) => (
                              <li key={point}>{point}</li>
                            ))}
                          </ul>
                        )}
                        <small>
                          出典: {selected.citationLabel ?? '未設定'} · 有効期間{' '}
                          {selected.effectiveFrom ?? '未設定'}〜{selected.validUntil ?? '未設定'}
                        </small>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
